import { useEffect, Fragment } from 'react';
import Head from 'next/head';
import { shape } from 'prop-types';
import { usePublisher } from '@magalu/mixer-publisher';
import {
  parseFiltersMetaTags,
  getFullTitle,
  removeSpacesAndAccents,
} from '@magalu/mixer-utils';
import Slice from '../Slice';
import Meta from '../Meta';
import { Container, StyleResets } from './globalStyles';

const CANONICAL_QUERY_PARAMS_ALLOWLIST = ['filters'];

const removeCanonicalQuery = canonical => {
  if (!canonical || typeof canonical !== 'string') return canonical;

  const [baseCanonical] = canonical.split('?');
  return baseCanonical;
};

const getCanonicalMeta = canonicalPath => {
  const [path = '', rawQuery = ''] = canonicalPath?.toLowerCase()?.split('?') || [];
  const searchParams = new URLSearchParams(rawQuery);
  const filteredSearchParams = new URLSearchParams();

  CANONICAL_QUERY_PARAMS_ALLOWLIST.forEach(param => {
    searchParams.getAll(param).forEach(value => {
      filteredSearchParams.append(param, value);
    });
  });

  const parameter = filteredSearchParams.toString();

  return {
    parameter: parameter ? `?${parameter}` : '',
    path: path.slice(1),
  };
};

const MainContainer = ({ structure, data, config, modules }) => {
  const { components, head, metaTags, layout = {}, ...pageInfo } = structure || {};
  const { publish } = usePublisher();
  const canonicalMeta = getCanonicalMeta(structure?.canonicalPath);
  const seoSearchCanonical = removeCanonicalQuery(
    data?.seoSearchContent?.meta?.canonical
  );

  useEffect(() => {
    if (structure?.name === 'conta') {
      publish('pageviewspace');
      return;
    }
    publish('pageview');
    publish('setpagebeforelogin', removeSpacesAndAccents(structure?.name));
  }, [JSON.stringify(structure)]);

  const hasLayout = Object.entries(layout).length;
  const Wrapper = hasLayout ? Container : Fragment;
  const wrapperProps = hasLayout ? { layout } : {};

  const renderSlice = slice => (
    <Slice
      key={slice.id || slice.grid_id}
      slice={slice}
      data={data}
      structure={pageInfo}
      config={config}
      utils={{ getFullTitle }}
      modules={modules}
    />
  );
  return (
    <>
      <Head>{head?.map(renderSlice)}</Head>
      <Wrapper {...wrapperProps}>
        <Meta
          tags={metaTags}
          data={{
            ...data,
            ...structure,
            meta: {
              alternate: structure?.config?.alternateDomain,
              canonical: structure?.config?.canonicalDomain,
              filters: {
                brand: parseFiltersMetaTags(data?.search?.filters, 'brand'),
              },
              ogImage: data?.product?.image
                ?.replace('{w}', structure?.config?.images?.width)
                ?.replace('{h}', structure?.config?.images?.height),
              parameter: canonicalMeta.parameter,
              path: canonicalMeta.path,
            },
            seoSearchContent: {
              ...data?.seoSearchContent,
              meta: {
                ...data?.seoSearchContent?.meta,
                canonical: seoSearchCanonical,
              },
            },
            utils: { getFullTitle },
          }}
        />
        <StyleResets />
        {components?.map(renderSlice)}
      </Wrapper>
    </>
  );
};

MainContainer.defaultProps = {
  config: {},
  modules: {},
};

MainContainer.propTypes = {
  config: shape({}),
  data: shape({}).isRequired,
  modules: shape({}),
  structure: shape({}).isRequired,
};

export default MainContainer;
